import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { withZod } from '@remix-validated-form/with-zod'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { SAVE_KOLLA_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboAsyncV2 from 'app/components/form/FormComboAsyncV2'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import FormInput from 'app/components/form/FormInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import { Tooltip } from 'app/components/Tooltip'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  kollaConnectorId: z.string().min(1, { message: `A Connector is required` }),
  kollaConsumerId: z.string().min(1, { message: `A Connector is required` }),
  kollaPracticeId: z.string().min(1, { message: `A Practice ID is required` }),
})

export const createKollaValidator = withZod(createSchema)

export function KollaFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    kollaConnectorId: LocationType[`kollaConnectorId`]
    kollaConsumerId: LocationType[`kollaConsumerId`]
    kollaPracticeId?: LocationType[`kollaPracticeId`]
  }
}) {
  const [kollaPractice, setKollaPractice] = useState<{ id: string; name: string; value: string } | undefined>(
    currentValues?.kollaPracticeId
      ? { id: currentValues.kollaPracticeId, name: currentValues.kollaPracticeId, value: currentValues.kollaPracticeId }
      : undefined,
  )
  const [kollaConsumerId, setKollaConsumerId] = useState<string>(currentValues?.kollaConsumerId || ``)
  const [kollaConnectorId, setKollaConnectorId] = useState<string>(currentValues?.kollaConnectorId || ``)
  const [practiceCount, setPracticeCount] = useState<number | null>(null)

  const onPracticesLoaded = (options: OptionType[]) => {
    setPracticeCount(options.length)
    if (currentValues?.kollaPracticeId) {
      const match = options.find((o) => o.id === currentValues.kollaPracticeId)
      if (match) setKollaPractice(match as { id: string; name: string; value: string })
    }
  }
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [confirm] = useConfirmation()

  // Debounce the free-text Consumer ID so the Practice combo only remounts/refetches
  // after typing settles, instead of firing /api/kolla/practices on every keystroke.
  const [debouncedConsumerId, setDebouncedConsumerId] = useState<string>(kollaConsumerId)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedConsumerId(kollaConsumerId), 400)
    return () => clearTimeout(t)
  }, [kollaConsumerId])

  useEffect(() => {
    debug(`kolla`, `values`, {
      kollaConsumerId,
      kollaConnectorId,
      kollaPractice,
    })
  }, [kollaConsumerId, kollaConnectorId, kollaPractice])

  useEffect(() => {
    setPracticeCount(null)
  }, [kollaConnectorId, kollaConsumerId])

  const onSave = async () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    if (!locationId || !kollaConsumerId) return

    const result = await gqlOpsMutation(SAVE_KOLLA_INTEGRATION, {
      locationId,
      kollaConsumerId,
      kollaConnectorId: kollaConnectorId || undefined,
      kollaPracticeId: kollaPractice?.id || undefined,
    })
    const data = result.data?.saveKollaIntegration
    if (data?.success) {
      notifySuccess(`Kolla integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove Kolla Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `kolla`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Kolla integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createKollaValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={6}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={6}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.kollaConnectorId,
                  tooltipForDisabledOption: `This location is already connected to Kolla`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}

        <FormSelect
          cols={6}
          name="kollaConnectorId"
          label="Connector ID"
          labelClassName="text-body-sm text-strong"
          defaultValue={kollaConnectorId}
          options={[``, `dentrix-g7-37603`, `eaglesoft-22012`, `opendental-91542`].map((connector) => ({
            label: connector,
            value: connector,
          }))}
          disabled={!!currentValues}
          newLineError={false}
          showErrorIcon
          onChange={(e) => {
            setKollaConnectorId(e.target.value)
          }}
        />
        <FormInput
          type="text"
          cols={6}
          name="kollaConsumerId"
          label="Consumer ID"
          labelClassName="text-body-sm text-strong"
          defaultValue={currentValues?.kollaConsumerId}
          disabled={!!currentValues}
          newLineError={false}
          showErrorIcon
          onChange={(e) => {
            setKollaConsumerId(e.target.value)
          }}
        />
        <FormHiddenInput name="kollaPracticeId" value={kollaPractice?.id} />
        <FormComboAsyncV2
          key={kollaConnectorId + debouncedConsumerId}
          name="_kollaPracticeId"
          label="Practice ID"
          queryURL="/api/kolla/practices"
          prefetch={!!debouncedConsumerId && !!kollaConnectorId}
          additionalParams={{ kollaConsumerId: debouncedConsumerId, kollaConnectorId }}
          inputClassName="!text-sm !font-normal !leading-5 !text-default"
          labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
          value={kollaPractice}
          onDataLoaded={onPracticesLoaded}
          labelChildren={
            practiceCount !== null ? (
              <span className="border-default ml-3 rounded border-[0.5px] bg-gray-minimal px-1.5 py-0.5 text-label-xs text-gray-strong">
                {`${practiceCount} ${practiceCount === 1 ? `practice` : `practices`}`}
              </span>
            ) : undefined
          }
          optionsMapper={(options) => {
            const none = { id: `None`, name: `None`, value: `None` }
            if (Array.isArray(options) && options.length) {
              return [
                none,
                ...options.map((option) => ({
                  id: `practice_${option.remote_id}`,
                  name: option.display_name || option.additional_data?.short_display_name,
                  value: `practice_${option.remote_id}`,
                })),
              ]
            }
            return [none]
          }}
          onChange={(value) => setKollaPractice(value)}
          cols={6}
        />
        <p className="col-span-6 -mt-2 text-body-sm text-subtle">
          Set a specific practice only for a multi-location install where this location maps to one practice. Otherwise
          choose None to bring in all patients from the consumer.
        </p>
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Tooltip
              message={
                currentValues
                  ? `Modifying kolla integration is not permitted. Please delete the integration and create a new one.`
                  : undefined
              }
              className={currentValues ? `cursor-not-allowed` : ``}
            >
              <Button
                type="submit"
                form="integration-form"
                label="Save"
                mode="primary"
                size="md"
                disabled={!!currentValues?.kollaConnectorId || (!selectedLocation && !currentValues?.locationId)}
                // onClick={() => setSaving(true)}
                // loading={saving}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  )
}
